import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, RefreshCw, Search, Save, X, Plus } from 'lucide-react';
import { analyticsAPI } from '../../../config/analytics-api';
import { settingsAPI } from '../../../config/settings-api';
import './Analytics.css';
import './AppSearches.css';

const PAGE_SIZE = 15;
const MAX_SUGGESTIONS = 6;

/** Inline editor for the admin-curated search hint list. */
function SearchSuggestionsEditor({ initialSuggestions = [], onSaved }) {
  const [items, setItems] = useState(() => (initialSuggestions.length ? initialSuggestions : ['']));
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  const suggestionsKey = JSON.stringify(initialSuggestions || []);
  useEffect(() => {
    let next = [];
    try {
      next = JSON.parse(suggestionsKey);
    } catch {
      next = [];
    }
    setItems(Array.isArray(next) && next.length ? next : ['']);
  }, [suggestionsKey]);

  const update = (i, val) => setItems((prev) => prev.map((v, idx) => (idx === i ? val : v)));
  const remove = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const add = () => {
    if (items.length < MAX_SUGGESTIONS) setItems((prev) => [...prev, '']);
  };

  const save = async () => {
    const cleaned = items.map((s) => s.trim()).filter(Boolean);
    setSaving(true);
    setSaveMsg(null);
    try {
      const result = await settingsAPI.updateSearchSuggestions(cleaned);
      if (result.success) {
        setSaveMsg({ type: 'success', text: 'Saved! App & webapp will use these hints.' });
        if (onSaved) onSaved(result.data?.searchSuggestionList || cleaned);
      } else {
        setSaveMsg({ type: 'error', text: result.msg || 'Failed to save' });
      }
    } catch (err) {
      setSaveMsg({ type: 'error', text: 'Failed to save suggestions' });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 4000);
    }
  };

  return (
    <div className="search-suggestions-editor">
      <div className="search-suggestions-editor-header">
        <div>
          <h3>Search Hint Suggestions</h3>
          <p>Set up to {MAX_SUGGESTIONS} placeholder hints shown in the app &amp; webapp grocery search bar. When set, these override the auto-trending list.</p>
        </div>
      </div>
      <div className="search-suggestions-list">
        {items.map((item, i) => (
          <div key={i} className="search-suggestions-item">
            <span className="search-suggestions-index">{i + 1}</span>
            <input
              type="text"
              value={item}
              onChange={(e) => update(i, e.target.value)}
              placeholder={`e.g. Coffee Powder`}
              className="search-suggestions-input"
              maxLength={60}
            />
            <button type="button" className="search-suggestions-remove" onClick={() => remove(i)} title="Remove">
              <X size={15} />
            </button>
          </div>
        ))}
      </div>
      <div className="search-suggestions-footer">
        {items.length < MAX_SUGGESTIONS && (
          <button type="button" className="search-suggestions-add" onClick={add}>
            <Plus size={15} /> Add hint
          </button>
        )}
        <button type="button" className="search-suggestions-save" onClick={save} disabled={saving}>
          {saving ? <Loader2 size={15} className="spin" /> : <Save size={15} />}
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {saveMsg && (
        <p className={`search-suggestions-msg ${saveMsg.type}`}>{saveMsg.text}</p>
      )}
    </div>
  );
}


const AppSearches = () => {
  const [page, setPage] = useState(1);
  const [searches, setSearches] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [expandedId, setExpandedId] = useState(null);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const [previewById, setPreviewById] = useState({});
  const [previewErrorById, setPreviewErrorById] = useState({});

  // Search suggestion list (from generalConfig)
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);

  // Load current search suggestion list from generalConfig on mount
  useEffect(() => {
    setSuggestionsLoading(true);
    settingsAPI.getAllGeneralConfigs()
      .then((result) => {
        if (result.success && result.data) {
          const list = result.data.searchSuggestionList;
          setSuggestions(Array.isArray(list) ? list.filter(Boolean) : []);
        }
      })
      .catch(() => { /* ignore */ })
      .finally(() => setSuggestionsLoading(false));
  }, []);

  const fetchSearches = useCallback(async (pageNum = 1) => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsAPI.getAppSearches(pageNum, PAGE_SIZE, 'all');
      if (response.success && response.data) {
        setSearches(response.data.searches || []);
        setTotalPages(response.data.totalPages || 0);
        setHasMore(Boolean(response.data.hasMore));
        setPage(response.data.page || pageNum);
      } else {
        setError(response.msg || 'Failed to load searches');
        setSearches([]);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load searches');
      setSearches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSearches(1);
  }, [fetchSearches]);

  const loadPreview = async (item) => {
    const id = item._id;
    if (previewById[id]) return;

    setPreviewLoadingId(id);
    setPreviewErrorById((prev) => ({ ...prev, [id]: null }));
    try {
      const response = await analyticsAPI.previewAppSearch(item.query);
      if (response.success) {
        setPreviewById((prev) => ({ ...prev, [id]: response.data || {} }));
      } else {
        setPreviewErrorById((prev) => ({ ...prev, [id]: response.msg || 'Search failed' }));
      }
    } catch (err) {
      console.error(err);
      setPreviewErrorById((prev) => ({ ...prev, [id]: 'Search failed' }));
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const toggleExpand = async (item) => {
    const id = item._id;
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    await loadPreview(item);
  };

  const renderPreview = (data) => {
    if (!data) return null;

    if (data.category && Array.isArray(data.products)) {
      return (
        <div className="app-searches-preview">
          <div className="app-searches-preview-meta">
            Category: <strong>{data.category}</strong>
            {typeof data.totalCount === 'number' ? ` · ${data.totalCount} products` : ''}
          </div>
          <div className="app-searches-product-grid">
            {data.products.map((product) => (
              <div key={product._id || product.productId} className="app-searches-product-card">
                <img
                  src={product.images?.[0]?.image || product.thumbnails?.[0] || '/placeholder.png'}
                  alt={product.name}
                  onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                />
                <div className="app-searches-product-info">
                  <p className="app-searches-product-name">{product.name}</p>
                  <p className="app-searches-product-price">QR {product.rate}</p>
                </div>
              </div>
            ))}
          </div>
          {!data.products.length && <p className="app-searches-empty">No products found</p>}
        </div>
      );
    }

    const results = Array.isArray(data.results) ? data.results : [];
    if (!results.length) {
      return <p className="app-searches-empty">No results for this query</p>;
    }

    return (
      <div className="app-searches-preview">
        {results.map((group) => (
          <div key={group.category || group._id} className="app-searches-result-group">
            <h4>{group.category || 'Results'}{typeof group.count === 'number' ? ` (${group.count})` : ''}</h4>
            <div className="app-searches-product-grid">
              {(group.products || []).map((product) => (
                <div key={product._id || product.productId} className="app-searches-product-card">
                  <img
                    src={product.images?.[0]?.image || product.thumbnails?.[0] || '/placeholder.png'}
                    alt={product.name}
                    onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                  />
                  <div className="app-searches-product-info">
                    <p className="app-searches-product-name">{product.name}</p>
                    <p className="app-searches-product-price">QR {product.rate}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="analytics-page app-searches-page">
      <div className="analytics-page-header">
        <div className="analytics-page-title">
          <h1>App Searches</h1>
          <p>Latest unique search queries from app and website</p>
        </div>
        <button type="button" className="app-searches-refresh" onClick={() => fetchSearches(page)} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spin' : ''} />
          Refresh
        </button>
      </div>

      {/* ── Search Hint Suggestions Editor ── */}
      <div className="analytics-section suggestions-section">
        {suggestionsLoading ? (
          <div className="app-searches-loading">
            <Loader2 className="spin" size={20} />
            Loading suggestions…
          </div>
        ) : (
          <SearchSuggestionsEditor
            initialSuggestions={suggestions}
            onSaved={(newList) => setSuggestions(newList)}
          />
        )}
      </div>

      <div className="analytics-section">
        <div className="analytics-section-title">
          <Search size={18} />
          Recent searches
        </div>

        {loading ? (
          <div className="app-searches-loading">
            <Loader2 className="spin" size={22} />
            Loading searches…
          </div>
        ) : error ? (
          <div className="users-error-state">
            <p>{error}</p>
            <button type="button" onClick={() => fetchSearches(page)} className="users-retry-button">
              <RefreshCw size={16} />
              Retry
            </button>
          </div>
        ) : searches.length === 0 ? (
          <p className="app-searches-empty">No search queries logged yet</p>
        ) : (
          <div className="app-searches-list">
            {searches.map((item) => {
              const isOpen = expandedId === item._id;
              const isPreviewLoading = previewLoadingId === item._id;
              return (
                <div key={item._id} className={`app-searches-row ${isOpen ? 'open' : ''}`}>
                  <button type="button" className="app-searches-row-main" onClick={() => toggleExpand(item)}>
                    <span className="app-searches-chevron">
                      {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </span>
                    <span className="app-searches-query">{item.query}</span>
                  </button>

                  {isOpen && (
                    <div className="app-searches-expand">
                      {isPreviewLoading && (
                        <div className="app-searches-loading compact">
                          <Loader2 className="spin" size={18} />
                          Loading search results…
                        </div>
                      )}
                      {!isPreviewLoading && previewErrorById[item._id] && (
                        <p className="app-searches-error">{previewErrorById[item._id]}</p>
                      )}
                      {!isPreviewLoading && !previewErrorById[item._id] && renderPreview(previewById[item._id])}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!loading && searches.length > 0 && (
          <div className="app-searches-pagination">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => fetchSearches(page - 1)}
            >
              Previous
            </button>
            <span>
              Page {page}{totalPages ? ` of ${totalPages}` : ''}
            </span>
            <button
              type="button"
              disabled={!hasMore}
              onClick={() => fetchSearches(page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppSearches;
